import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import * as d3 from "d3";
import { getTableValue, getTableAvgValue, getSumValue, fieldTotal } from "./utils";

const timeFormat = d3.timeFormat("%Y-%m-%d %H:%M:%S");

const CurrentTable = styled.table`
  thead th {
    padding: 2px 10px !important;

    &.sorting {
      padding: 2px 20px 2px 10px !important;
    }
    &.select {
    padding: 2px 0px 2px 3px !important;
    }
  }

  tbody td {
    padding: 1px 10px 2px !important;
  }

  tbody td.number {
    text-align: right;
    width: 50px;
  }
  tbody td.select {
    padding: 2px 0px !important;
    display: flex;
    justify-content: center;
  }
`;

const Wrapper = styled.div`
  width: 100%;
`;

const Header = styled.h4`
  font-size: 11px !important;
  margin: 0 0 16px;
  text-align: left;
  width: 100%;
  text-wrap: nowrap;
`;

const SubColor = styled.div`
  background-color: ${(props) => props.bgColor};
  width: 12px;
  height: 12px;
  border: 2px solid red;
  border-color:  ${(props) => props.strokeColor};
  margin-top: 2px;
  cursor: pointer;
`;

const SubNameWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const SubName = styled.div`
  text-overflow: ellipsis;
  cursor: pointer;
  overflow: hidden;
  width: 170px;
  white-space: nowrap;
  font-family: AllianceNo2-SemiBold !important;
  font-weigth: 100;
`;

const CurrentDate = styled.div`
  font-size: 11px;
  text-align: right;
  margin-top: 4px;
`;

function getSortedFields(
  fields,
  filterFields,
  current,
  { type, direction },
  upDownLink
) {
  const tableFields = [...fields].reduce((acc, field) => {
    const { name } = field;
    if (filterFields.has(name) && name !== "rest") {
      const link = current && current[name] && (current[name][`${upDownLink}link`]);
      const rtt = current && current[name] && (current[name][`${upDownLink}rtt`]);
      const rtx = current && current[name] && (current[name][`${upDownLink}rtx`]);
      acc.push({ ...field, current: link, rtt, rtx});
    }
    return acc;
  }, []);
  const top10 = tableFields.slice(0, 9);
  if (type === "av" && direction === "desc") {
    if (upDownLink === "up") {
      return top10.sort((a, b) => {
        return getSumValue(b.totalUp) - getSumValue(a.totalUp);
      });
    } else {
      return top10.sort((a, b) => {
        return getSumValue(b.totalDown) - getSumValue(a.totalDown);
      });
    }
  }
  if (type === "av" && direction === "asc") {
    if (upDownLink === "up") {
      return top10.sort((a, b) => {
        return getSumValue(a.totalUp) - getSumValue(b.totalUp);
      });
    } else {
      return top10.sort((a, b) => {
        return getSumValue(a.totalDown) - getSumValue(b.totalDown);
      });
    }
  }
  if (type === "cur" && direction === "desc") {
    return top10.sort((a, b) => {
      return getSumValue(b.current) - getSumValue(a.current);
    });
  }
  if (type === "cur" && direction === "asc") {
    return top10.sort((a, b) => {
      return getSumValue(a.current) - getSumValue(b.current);
    });
  }
  if (type === "rtt" && direction === "desc") {
    return top10.sort((a, b) => {
      return getSumValue(b.rtt) - getSumValue(a.rtt);
    });
  }
  if (type === "rtt" && direction === "asc") {
    return top10.sort((a, b) => {
      return getSumValue(a.rtt) - getSumValue(b.rtt);
    });
  }
  if (type === "rtx" && direction === "desc") {
    return top10.sort((a, b) => {
      return getSumValue(b.rtx) - getSumValue(a.rtx);
    });
  }
  if (type === "rtx" && direction === "asc") {
    return top10.sort((a, b) => {
      return getSumValue(a.rtx) - getSumValue(b.rtx);
    });
  }

  return top10;
}

const CurrentDataTable = ({
  fields,
  headerOutput,
  type,
  current,
  filterFields,
  onSeriesInspect,
  total,
  initTime,
  seconds,
  plotSelection,
  setPlotSelection
}) => {
  const totalLink = useRef(0);
  const secondsExact = useRef(0);
  
  const [sorting, setSorting] = useState({ type: "av", direction: "desc" });

  useEffect(() => {
    totalLink.current += getSumValue(total.link);
    secondsExact.current = (Date.now()-initTime)/1000;
  }, [seconds]);

  useEffect(() => {
    totalLink.current = 0;
  }, [initTime]);

  const sortedFields = getSortedFields(
    fields,
    filterFields,
    current,
    sorting,
    type
  );

  if (sortedFields.length === 0) {
    return null;
  }

  const handleClick = (type) => {
    setSorting({
      type,
      direction: sorting.direction === "asc" ? "desc" : "asc",
    });
  };

  const plotSelectionSet = new Set(plotSelection);
  const handlePlotSelect = (name)=> {
    if(plotSelectionSet.has(name)){
      plotSelectionSet.delete(name);
      setPlotSelection([...plotSelectionSet])
    } else {
      setPlotSelection([...plotSelection, name])
    }    
  }

  return (
    <Wrapper>
      {current ? <Header>Current Speed (Mbps), Average Speed (Mbps), Latency (ms) & Retransmissions (%)</Header> : null}
      <CurrentTable className="table dataTable">
        <thead>
          <tr>
            <th className="select">PLOT</th>
            <th>{headerOutput.toUpperCase()}</th>
            <th
              className={
                sorting.type === "cur"
                  ? `sorting_${sorting.direction}`
                  : "sorting"
              }
              onClick={() => handleClick("cur")}
            >
              CURR.
            </th>
            <th
              className={
                sorting.type === "av"
                  ? `sorting_${sorting.direction}`
                  : "sorting"
              }
              onClick={() => handleClick("av")}
            >
              AVG.
            </th>
            <th
              className={
                sorting.type === "rtx"
                  ? `sorting_${sorting.direction}`
                  : "sorting"
              }
              onClick={() => handleClick("rtx")}
            >
              LAT.
            </th>
            <th
              className={
                sorting.type === "lat"
                  ? `sorting_${sorting.direction}`
                  : "sorting"
              }
              onClick={() => handleClick("lat")}
            >
              RTX.
            </th>
          </tr>
        </thead>
        <tbody>
          <tr key="total">
            <td className="select"><SubColor bgColor={fieldTotal.color} strokeColor={fieldTotal.color}></SubColor></td>
            <td>
              <SubNameWrapper>
                <SubName>{fieldTotal.name}</SubName>
              </SubNameWrapper>
            </td>
            <td className="number">{getTableValue(total.link)}</td>
            <td className="number">{getTableAvgValue(totalLink.current, secondsExact.current)}</td>
            <td className="number">{getTableValue(total.rtt)}</td>
            <td className="number">{getTableValue(total.rtx)}</td>
          </tr>
          {sortedFields.map(({ name, totalDown, totalUp, current, rtx, rtt, color }) => {
            return (
              <tr key={name}>
                <td  className="select" onClick={() => handlePlotSelect(name)}>
                  <SubColor bgColor={plotSelectionSet.has(name) ? color : "transparent"} strokeColor={color}></SubColor>
                </td>
                <td>
                  <SubName title={name} onClick={() => onSeriesInspect(name)}>
                    <a
                      href="#"
                      id="addr"
                      data-toggle="tooltip"
                      data-placement="top"
                      title={name}
                    >
                      {name}
                    </a>
                  </SubName>
                </td>
                <td className="number">{getTableValue(current)}</td>
                <td className="number">
                  {type === "down"
                    ? getTableAvgValue(totalDown, secondsExact.current)
                    : getTableAvgValue(totalUp, secondsExact.current)}
                </td>
                <td className="number">{getTableValue(rtt)}</td>
                <td className="number">{getTableValue(rtx)}</td>
              </tr>
            );
          })}
          
        </tbody>
      </CurrentTable>
      <CurrentDate>{timeFormat(current.time)}</CurrentDate>
    </Wrapper>
  );
};

export default CurrentDataTable;
